import { Skeleton } from '@brew/ui/skeleton'
import type { CSSProperties, ReactNode } from 'react'
import { resolveExactImportHtmlState } from '@/lib/rendering/exact-import-html-state'

import { EmailJsxPreview } from './email-jsx-preview-dynamic'
import { ScaledEmailHtmlFrame } from './scaled-email-html-frame'

export interface EmailDesignPreviewProps {
  /** React Email JSX source of the design (used to detect imports). */
  jsx?: string | null
  /** Persisted/delivered HTML document — authoritative for imports. */
  html?: string | null
  /**
   * Frame title / accessibility label: the design's own title or subject
   * (data, so this shared server/client component needs no catalog).
   */
  title: string
  /** Downscale-only fit to the host column (default true). */
  fitToWidth?: boolean
  /**
   * Import lane: lay a narrower email out at the host column's width so its
   * own background fills the column (as an email client shows it) instead
   * of leaving gutters. The authored lane already fills. Wider emails still
   * downscale.
   */
  fillWidth?: boolean
  className?: string
  style?: CSSProperties
  /** Rendered when there is nothing renderable. */
  fallback?: ReactNode
  /** Authored lane only: JSX present but unparseable. */
  brokenState?: ReactNode
  /**
   * Deterministic import whose source HTML is unavailable. Defaults to
   * `fallback` — never the reconstructed preview.
   */
  htmlMissingState?: ReactNode
  /**
   * Whether the `html` slot has finished resolving. Server-fetched rows
   * carry their HTML inline, so this defaults to true; live-query hosts
   * that resolve HTML separately pass false to show a placeholder instead
   * of a false "missing" state.
   */
  isHtmlResolved?: boolean
  /** Height reserved by the HTML lane before its first measurement. */
  unmeasuredMinHeight?: number
  /**
   * Import lane only: observes the resolved design width (parsed guess,
   * then any live measurement adopted over it).
   */
  onIntrinsicWidthResolved?: (width: number) => void
}

/**
 * Read-only preview dispatcher for a persisted email design — the ONE
 * place that decides which engine a surface outside the canvas renders
 * with, mirroring `resolveExactImportHtmlState`:
 *
 * - Deterministic imports (`data-brew-import`) render their
 *   source-preserved HTML in an isolated iframe and NEVER fall through to
 *   the reconstructed TipTap preview (imported emails rely on `<style>`
 *   blocks, table layout, and width-scoped media queries the
 *   reconstruction cannot express).
 * - Authored designs render the lightweight view-only TipTap
 *   reconstruction (`EmailJsxPreview`), the same engine posture as the
 *   canvas editor.
 *
 * Shared component (no 'use client'): from an RSC host the import lane's
 * srcDoc lands in the server markup, while the TipTap lane stays behind
 * its existing `ssr: false` dynamic shim.
 */
export function EmailDesignPreview({
  jsx,
  html,
  title,
  fitToWidth = true,
  fillWidth = false,
  className,
  style,
  fallback,
  brokenState,
  htmlMissingState,
  isHtmlResolved = true,
  unmeasuredMinHeight,
  onIntrinsicWidthResolved,
}: EmailDesignPreviewProps) {
  const importState = resolveExactImportHtmlState({
    jsxSource: jsx,
    htmlSource: html,
    isHtmlResolved,
  })

  if (importState === 'ready') {
    return (
      <ScaledEmailHtmlFrame
        html={html as string}
        title={title}
        fitToWidth={fitToWidth}
        fillWidth={fillWidth}
        className={className}
        style={style}
        unmeasuredMinHeight={unmeasuredMinHeight}
        onIntrinsicWidthResolved={onIntrinsicWidthResolved}
      />
    )
  }
  if (importState === 'loading') {
    return <Skeleton className="h-[480px] w-full" />
  }
  if (importState === 'missing') {
    return <>{htmlMissingState ?? fallback ?? null}</>
  }

  // Authored design ('not-applicable').
  if (jsx) {
    return (
      <EmailJsxPreview
        jsx={jsx}
        fitToWidth={fitToWidth}
        className={className}
        style={style}
        fallback={fallback}
        brokenState={brokenState}
      />
    )
  }
  if (html && html.trim().length > 0) {
    return (
      <ScaledEmailHtmlFrame
        html={html}
        title={title}
        fitToWidth={fitToWidth}
        fillWidth={fillWidth}
        className={className}
        style={style}
        unmeasuredMinHeight={unmeasuredMinHeight}
        onIntrinsicWidthResolved={onIntrinsicWidthResolved}
      />
    )
  }
  return <>{fallback ?? null}</>
}
